import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { API_OPT_OUT, TOKEN_PARAM, VIEW_LOGIN, VIEW_ACCOUNT } from '../../common/EsConstants';
import { useAuth } from '../parent/EsAuth';
import { esPost } from '../../common/EsFetch';
import { useEsStrings } from '../../common/EsStrings';

/**
 * "Opt-out of Escapade mailings", landed on from the foot of one.
 *
 * <h2>⚠️ TWO KINDS OF READER, AND THE DIFFERENCE IS THE WHOLE DESIGN</h2>
 *
 * <p><b>A registered reader has to sign in.</b> Their mailing carries no token,
 * and this page sends them to the login page if they are not already there. The
 * reason is that <b>an email gets forwarded</b>: a link that worked without a
 * session would let whoever received the forward opt the original reader out,
 * and they would never find out — the only symptom is mail that stops
 * arriving. Tim caught that on 2026-09-18.
 *
 * <p><b>A mailing-list joiner cannot sign in at all</b> — no password, no
 * Cognito user, nothing to present — so requiring it would mean they can never
 * leave, which is what CAN-SPAM prohibits and what spam filters score a sending
 * domain on. Their mailing carries a token and this page uses it without a
 * session.
 *
 * <p>Somebody sent to the login comes back HERE, the opt-out posts on mount
 * with their session, and then the page replaces itself with their ACCOUNT PAGE,
 * where the same checkbox shows the state it is now in. Tim, 2026-10-01: "It
 * should opt out, then show the account page." The first version sent them
 * straight to the account page after the login, which never opted anybody out.
 * A joiner with a token has no account page and stays on the sentence.
 *
 * <p>So: <b>token in the URL → post it. No token → require a login.</b> The
 * page is not wrapped in RequireAuth, because that would refuse exactly the
 * people the token exists for; it does the same job itself, one branch later.
 *
 * <p>The token cannot be turned on a registered account:
 * {@code EsAccountService.optOutByToken} refuses any row that is not a
 * joiner, and no registered reader's mailing carries one to begin with.
 *
 * <h2>WHY THIS PAGE EXISTS AT ALL, RATHER THAN A LINK STRAIGHT TO THE API</h2>
 *
 * <p>Tim asked for one click, and this is one click once you are signed in.
 * What it is NOT is a GET that opts out, and the difference is the second
 * half of the safety: mail clients and security scanners prefetch links in
 * messages. A GET endpoint behind that link would be one prefetch away from
 * opting somebody out who never pressed anything.
 *
 * <p>So the link lands here and the POST happens on MOUNT. A scanner that
 * fetches the URL gets HTML and runs no script. A person who clicks gets the
 * sentence, having pressed exactly once.
 *
 * <h2>It says the same thing whatever happened</h2>
 *
 * <p>Already opted out and just opted out both get "You will no longer receive
 * Escapade mailings." There is no "you were already opted out" state,
 * because it is not a state the person asked about and the sentence is true
 * either way.
 *
 * <p>A FAILED request is different and is shown as one. That is the network or
 * the server being down, not an answer about this token, and telling somebody
 * they are opted out when the request never landed would be the one lie this
 * page could tell.
 */
export default function EsOptOut() {
    const t = useEsStrings();

    const [ params ] = useSearchParams();
    const auth = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const [ state, setState ] = useState( 'working' );
    const [ message, setMessage ] = useState( '' );

    const aToken = params.get( TOKEN_PARAM ) ?? '';

    // StrictMode double-mounts effects in development, and this one WRITES.
    // The write is idempotent server-side, so a second post changes nothing --
    // but it would put a second history line against somebody's name, and a
    // guard costs one ref.
    const sentRef = useRef( false );

    // Whether this visit may post at all. A token stands in for a session; with
    // neither there is nobody to opt out and the redirect below happens first.
    const aMayPost = Boolean( aToken ) || Boolean( auth.user );

    useEffect( () => {
        if ( !aMayPost || sentRef.current ) return;
        sentRef.current = true;

        // The token, or nothing at all. NEVER an account id by either route: a
        // parameter naming a row is the forwarded-link hole rebuilt one layer
        // down, since a caller could name somebody else's.
        esPost( API_OPT_OUT, aToken ? { token: aToken } : {} )
            .then( ( pBody ) => {
                // null when esback said nothing: the render supplies the sentence, in the reader's locale.
                setMessage( pBody?.message ?? null );
                setState( 'done' );

                // A session reader has an account page to read the result off; a
                // token holder has none, so they stay and read the sentence.
                if ( !aToken ) void navigate( VIEW_ACCOUNT, { replace: true } );
            } )
            .catch( () => setState( 'failed' ) );
    }, [ aMayPost, aToken, navigate ] );

    // Same two states RequireAuth distinguishes, and for the same reason: on a
    // hard load -- which every visit from an email is -- the first render has
    // no user yet, and treating that as "no user" would bounce a signed-in
    // reader to the login page before esback had been asked anything.
    if ( auth.loading ) return null;

    if ( !aMayPost ) {
        // Back HERE after signing in -- the opt-out is what they asked for, and
        // the mount effect above posts it and then moves them to the account page.
        // Sending them to the account page directly (the 2026-09-18 shape) opted
        // nobody out, because nothing on that page posts.
        //
        // EsLogin reads location.state.from and prefers it over the welcome page.
        return <Navigate to={ VIEW_LOGIN } state={ { from: location } } replace />;
    }

    return (
        <section data-testid="es-opt-out-view" className="relative z-10 pb-4 pt-[160px]">
            <div className="container">
                <div className="mx-auto max-w-[600px] text-center">
                    <h1 data-testid="es-opt-out-heading"
                        className="mb-6 text-3xl font-bold text-heading">
                        { t( 'account-opt-out-heading', 'Escapade mailings' ) }
                    </h1>

                    { state === 'working' &&
                        <p data-testid="es-opt-out-working" className="text-body">
                            { t( 'account-opt-out-working', 'One moment…' ) }
                        </p> }

                    { state === 'done' &&
                        <p data-testid="es-opt-out-done" className="text-lg text-body">
                            { message ?? t( 'account-opt-out-done', 'You will no longer receive Escapade mailings.' ) }
                        </p> }

                    { state === 'failed' &&
                        <p data-testid="es-opt-out-error" className="text-lg text-danger">
                            { /* Not "you are opted out". The request never landed,
                                 so we do not know that -- and saying it would be the
                                 one lie this page could tell somebody who then stops
                                 reading the mail they still receive. */ }
                            { t( 'account-opt-out-failed', 'That did not go through. Please try the link again, or change it on your account page if you have an account.' ) }
                        </p> }
                </div>
            </div>
        </section>
    );
}
